/**
 * WebRTC P2P 数据通道与边缘中继双通道融合传输层 (Dual-Channel P2P & Cloudflare Edge Relay)
 * 职责：
 * 1. 建立 WebRTC RTCPeerConnection (STUN: Cloudflare + Google)
 * 2. 自动握手 DataChannel，打通点对点毫秒级直连
 * 3. 智能冗余降级：在 NAT 对称穿透失败或连接建立期，100% 无缝切换到 Cloudflare Worker WebSocket 边缘中继
 * 4. 实时端到端 RTT 延迟探测与链路状态反馈
 */

import { SignalingClient } from "./signaling";
import type { PKGamePacket } from "../../shared/protocol";

export type ChannelMode = "idle" | "connecting" | "p2p" | "cf_relay" | "closed";

export interface ChannelStatus {
  mode: ChannelMode;
  rtt: number; // ms
  isReady: boolean;
}

export type PacketHandler = (packet: PKGamePacket, fromPeerId?: string) => void;
export type StatusHandler = (status: ChannelStatus) => void;

const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.cloudflare.com:3478" },
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

export class P2PChannel {
  private signaling: SignalingClient;
  private peerConn: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private role: "host" | "guest" = "host";
  private packetHandlers: PacketHandler[] = [];
  private statusHandlers: StatusHandler[] = [];
  private currentMode: ChannelMode = "idle";
  private currentRtt: number = 0;
  private p2pPingInterval: number | null = null;
  private isDestroyed = false;

  constructor(signaling: SignalingClient) {
    this.signaling = signaling;
    this.setupSignalingListeners();
  }

  public onPacket(handler: PacketHandler) {
    this.packetHandlers.push(handler);
  }

  public onStatus(handler: StatusHandler) {
    this.statusHandlers.push(handler);
    handler({ mode: this.currentMode, rtt: this.currentRtt, isReady: this.isReady() });
  }

  public isReady(): boolean {
    return this.currentMode === "p2p" || this.currentMode === "cf_relay";
  }

  private updateStatus(mode: ChannelMode, rtt?: number) {
    this.currentMode = mode;
    if (typeof rtt === "number") this.currentRtt = rtt;
    const status: ChannelStatus = {
      mode: this.currentMode,
      rtt: this.currentRtt,
      isReady: this.isReady(),
    };
    for (const h of this.statusHandlers) {
      h(status);
    }
  }

  private setupSignalingListeners() {
    this.signaling.on("signal_offer", async (offer) => {
      if (this.role === "guest" && this.peerConn) {
        try {
          await this.peerConn.setRemoteDescription(new RTCSessionDescription(offer));
          const answer = await this.peerConn.createAnswer();
          await this.peerConn.setLocalDescription(answer);
          this.signaling.sendAnswer(answer);
        } catch (err) {
          console.warn("[P2P] Handling offer failed, fallback to relay", err);
          this.updateStatus("cf_relay");
        }
      }
    });

    this.signaling.on("signal_answer", async (answer) => {
      if (this.role === "host" && this.peerConn) {
        try {
          await this.peerConn.setRemoteDescription(new RTCSessionDescription(answer));
        } catch (err) {
          console.warn("[P2P] Handling answer failed, fallback to relay", err);
          this.updateStatus("cf_relay");
        }
      }
    });

    this.signaling.on("signal_ice", async (candidate) => {
      if (this.peerConn && candidate) {
        try {
          await this.peerConn.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.warn("[P2P] Add ICE candidate failed", err);
        }
      }
    });

    this.signaling.on("relay_message", (packet, fromPeerId) => {
      // 收到 Cloudflare 边缘中继的数据包（附带服务端认证的真实发送者 peerId）
      this.handleIncomingPacket(packet, "cf_relay", fromPeerId);
    });

    this.signaling.on("latency_update", (latency) => {
      if (this.currentMode === "cf_relay" || this.currentMode === "connecting") {
        this.updateStatus(this.currentMode, latency);
      }
    });
  }

  public initConnection(role: "host" | "guest") {
    this.cleanupPeer();
    this.role = role;
    this.isDestroyed = false;
    this.updateStatus("connecting");

    try {
      this.peerConn = new RTCPeerConnection({
        iceServers: ICE_SERVERS,
      });

      this.peerConn.onicecandidate = (event) => {
        if (event.candidate) {
          this.signaling.sendIceCandidate(event.candidate);
        }
      };

      this.peerConn.oniceconnectionstatechange = () => {
        const state = this.peerConn?.iceConnectionState;
        if (state === "failed" || state === "disconnected") {
          // P2P 穿透受阻，直接切入 Cloudflare 边缘 WebSocket 中继
          this.updateStatus("cf_relay");
        }
      };

      if (role === "host") {
        // Host 主动创建数据通道并生成 Offer
        this.dataChannel = this.peerConn.createDataChannel("pk2048-sync", {
          ordered: true,
        });
        this.setupDataChannel(this.dataChannel);

        this.peerConn
          .createOffer()
          .then((offer) => {
            return this.peerConn!.setLocalDescription(offer);
          })
          .then(() => {
            this.signaling.sendOffer(this.peerConn!.localDescription);
          })
          .catch((err) => {
            console.warn("[P2P] Create offer failed, use relay", err);
            this.updateStatus("cf_relay");
          });
      } else {
        // Guest 监听通道被动接入
        this.peerConn.ondatachannel = (event) => {
          this.dataChannel = event.channel;
          this.setupDataChannel(this.dataChannel);
        };
      }

      // 4 秒超时保底：若 WebRTC 尚未连通，立即激活 Cloudflare Relay 模式保障游戏开始
      window.setTimeout(() => {
        if (this.currentMode === "connecting") {
          this.updateStatus("cf_relay");
        }
      }, 4000);
    } catch (err) {
      console.warn("[P2P] WebRTC initialization failed, use relay mode", err);
      this.updateStatus("cf_relay");
    }
  }

  private setupDataChannel(channel: RTCDataChannel) {
    channel.onopen = () => {
      this.updateStatus("p2p");
      this.startP2PPing();
    };

    channel.onclose = () => {
      this.stopP2PPing();
      if (!this.isDestroyed) {
        this.updateStatus("cf_relay");
      }
    };

    channel.onerror = (err) => {
      console.warn("[P2P] DataChannel error", err);
      this.updateStatus("cf_relay");
    };

    channel.onmessage = (event) => {
      try {
        const packet = JSON.parse(event.data) as PKGamePacket;
        this.handleIncomingPacket(packet, "p2p");
      } catch (err) {
        console.error("[P2P] Packet parse error", err);
      }
    };
  }

  private handleIncomingPacket(
    packet: PKGamePacket,
    source: "p2p" | "cf_relay",
    fromPeerId?: string
  ) {
    if (packet.type === "ping_p2p") {
      this.sendPacket({ type: "pong_p2p", timestamp: packet.timestamp });
      return;
    }

    if (packet.type === "pong_p2p") {
      const rtt = Math.max(1, Date.now() - packet.timestamp);
      this.updateStatus(source, rtt);
      return;
    }

    for (const h of this.packetHandlers) {
      try {
        h(packet, fromPeerId);
      } catch (err) {
        console.error("[P2P] Packet handler error", err);
      }
    }
  }

  /**
   * 智能多路广播与定向投递：通过 Cloudflare Edge WebSocket 多人房间底座实时分发给所有队友、对手与观战席
   */
  public sendPacket(packet: PKGamePacket, targetPeerId?: string) {
    this.signaling.sendRelay(packet, targetPeerId);
  }

  private startP2PPing() {
    this.stopP2PPing();
    this.p2pPingInterval = window.setInterval(() => {
      if (this.dataChannel && this.dataChannel.readyState === "open") {
        this.sendPacket({ type: "ping_p2p", timestamp: Date.now() });
      }
    }, 2500);
  }

  private stopP2PPing() {
    if (this.p2pPingInterval !== null) {
      clearInterval(this.p2pPingInterval);
      this.p2pPingInterval = null;
    }
  }

  public cleanupPeer() {
    this.stopP2PPing();
    if (this.dataChannel) {
      try {
        this.dataChannel.close();
      } catch (_) {}
      this.dataChannel = null;
    }
    if (this.peerConn) {
      try {
        this.peerConn.close();
      } catch (_) {}
      this.peerConn = null;
    }
  }

  public destroy() {
    this.isDestroyed = true;
    this.cleanupPeer();
    this.updateStatus("closed");
  }
}

